import React from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Select,
  MenuItem,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

interface Schedule {
  id?: string;
  days: number[];
  startTime: number;
  endTime: number;
  minimumMeetingLength: number;
  maximumMeetingLength: number;
  defaultMeetingLength: number;
  minutesBetweenMeetings: number;
}

interface WebTimeSlotProps {
  slot: Schedule;
  onTimePress: (slot: Schedule, isStartTime: boolean) => void;
  onAdd: () => void;
  onRemove: () => void;
  onChangeDuration: (newDuration: string) => void;
}

const WebTimeSlot: React.FC<WebTimeSlotProps> = ({
  slot,
  onTimePress,
  onAdd,
  onRemove,
  onChangeDuration,
}) => {
  const theme = useTheme();

  const formatTime = (minutes: number): string => {
    if (minutes < 0 || isNaN(minutes)) {
      return '--:--';
    }

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

    return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
  };

  const generateDurationButtons = () => {
    // @ts-ignore
    const globalSettings = global?.appSettings?.defaultSchedule?.default?.[0];
    const maxMeetingLength = Math.max(
      globalSettings?.maximumMeetingLength || 300,
      300
    );

    const availableDurations = [];
    for (let duration = 5; duration <= maxMeetingLength; duration += 5) {
      availableDurations.push(duration);
    }

    return availableDurations;
  };

  const durationOptions = generateDurationButtons();

  const handleDurationSelect = (duration: number) => {
    onChangeDuration(duration.toString());
  };

  return (
    <Paper
      elevation={1}
      sx={{
        p: 3,
        mb: 2,
        backgroundColor: theme.custom.background2,
        border: `1px solid ${theme.custom.stroke}`,
        borderRadius: 3,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography
            variant="body2"
            sx={{
              color: theme.custom.textLight,
              fontWeight: 500,
              minWidth: 40,
            }}
          >
            From
          </Typography>

          <Button
            variant="outlined"
            onClick={() => onTimePress(slot, true)}
            sx={{
              minWidth: '100px',
              height: '40px',
              color: theme.custom.text,
              borderColor: theme.custom.stroke,
              backgroundColor: theme.custom.background3,
              fontWeight: 600,
              fontSize: '14px',
              borderRadius: 2,
              '&:hover': {
                borderColor: theme.custom.primary,
                backgroundColor: theme.custom.fill1,
              },
            }}
          >
            {formatTime(slot.startTime)}
          </Button>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography
            variant="body2"
            sx={{
              color: theme.custom.textLight,
              fontWeight: 500,
              minWidth: 35,
            }}
          >
            Until
          </Typography>

          <Button
            variant="outlined"
            onClick={() => onTimePress(slot, false)}
            sx={{
              minWidth: '100px',
              height: '40px',
              color: theme.custom.text,
              borderColor: theme.custom.stroke,
              backgroundColor: theme.custom.background3,
              fontWeight: 600,
              fontSize: '14px',
              borderRadius: 2,
              '&:hover': {
                borderColor: theme.custom.primary,
                backgroundColor: theme.custom.fill1,
              },
            }}
          >
            {formatTime(slot.endTime)}
          </Button>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography
            variant="body2"
            sx={{
              color: theme.custom.textLight,
              fontWeight: 500,
              minWidth: 80,
            }}
          >
            Slot Duration
          </Typography>

          <Select
            value={slot.defaultMeetingLength}
            onChange={(e) => handleDurationSelect(Number(e.target.value))}
            sx={{
              width: '120px',
              height: '40px',
              backgroundColor: theme.custom.background3,
              color: theme.custom.text,
              borderRadius: 2,
              fontSize: '14px',
              fontWeight: 500,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.custom.stroke,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.custom.primary,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.custom.primary,
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 300,
                  backgroundColor: theme.custom.background2,
                  border: `1px solid ${theme.custom.stroke}`,
                  '& .MuiMenuItem-root': {
                    fontSize: '14px',
                    color: theme.custom.text,
                    '&:hover': {
                      backgroundColor: theme.custom.fill1,
                    },
                    '&.Mui-selected': {
                      backgroundColor: theme.custom.primaryLight,
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: theme.custom.primaryLight,
                      },
                    },
                  },
                },
              },
            }}
          >
            {durationOptions.map((duration) => (
              <MenuItem key={duration} value={duration}>
                {duration} min
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <IconButton
            onClick={onAdd}
            size="small"
            sx={{
              color: theme.custom.primary,
              backgroundColor: 'transparent',
              border: `1px solid ${theme.custom.primary}`,
              borderRadius: 2,
              width: 36,
              height: 36,
              '&:hover': {
                backgroundColor: theme.custom.primaryLight,
              },
            }}
          >
            <Add fontSize="small" />
          </IconButton>

          <IconButton
            onClick={onRemove}
            size="small"
            sx={{
              color: theme.custom.textLight,
              backgroundColor: 'transparent',
              border: `1px solid ${theme.custom.stroke}`,
              borderRadius: 2,
              width: 36,
              height: 36,
              '&:hover': {
                backgroundColor: theme.custom.fill1,
                color: '#d32f2f',
                borderColor: '#d32f2f',
              },
            }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
};

export default WebTimeSlot;
